import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createMatchSummary,
  deleteMatchSummary,
  getMatchSummaries,
  updateMatchSummary,
  type ApiError,
} from "@/lib/api";
import { invalidateScope, useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";
import type {
  CreateMatchSummaryRequest,
  MatchSummary,
  UpdateMatchSummaryRequest,
} from "@/lib/types";

/**
 * Coach > Match Summaries list-page data hooks (M6a).
 *
 * Like Playlists — and unlike Notes/Clips — the match-summary
 * **composer is its own modal** (no Coach Review surface), so
 * `useMatchSummaryMutations` bundles all three writes: create /
 * update / delete. Each `invalidateScope`s on success.
 *
 * Scope-keyed + gated on `teamId`, like the other M6a list
 * queries.
 */

export function useCoachSummariesList() {
  const teamId = useScopeStore((s) => s.teamId);
  return useQuery({
    queryKey: useScopedKey("match-summaries"),
    queryFn: getMatchSummaries,
    enabled: !!teamId,
  });
}

export type MatchSummaryMutations = {
  createSummary: (
    body: CreateMatchSummaryRequest,
  ) => Promise<{ summary: MatchSummary }>;
  updateSummary: (
    id: number,
    body: UpdateMatchSummaryRequest,
  ) => Promise<{ summary: MatchSummary }>;
  deleteSummary: (id: number) => Promise<{ ok: true }>;
  isPending: boolean;
  error: ApiError | null;
};

export function useMatchSummaryMutations(): MatchSummaryMutations {
  const create = useMutation({
    mutationFn: createMatchSummary,
    onSuccess: () => invalidateScope(),
  });
  const update = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: UpdateMatchSummaryRequest;
    }) => updateMatchSummary(id, body),
    onSuccess: () => invalidateScope(),
  });
  const remove = useMutation({
    mutationFn: deleteMatchSummary,
    onSuccess: () => invalidateScope(),
  });

  const all = [create, update, remove];
  return {
    createSummary: (body) => create.mutateAsync(body),
    updateSummary: (id, body) => update.mutateAsync({ id, body }),
    deleteSummary: (id) => remove.mutateAsync(id),
    isPending: all.some((m) => m.isPending),
    error: (all.find((m) => m.error)?.error as ApiError | undefined) ?? null,
  };
}
